import { ApiClientConstant } from '@cureskin/api-client/';

/**
 * The two "for eg." lines the location bottomsheet shows, picked from the user's primary concern
 * and its class, so the examples always name a climate factor behind the concern just selected.
 *
 * The two icons are fixed — only the sentences change per concern.
 */
const EXAMPLE_ICONS: [string, string] = ['/assets/icons/delhi.svg', '/assets/icons/bangalore.svg'];

/** One rendered card: a fixed icon plus its translated sentence. */
export interface LocationExample {
  iconSrc: string;
  text: string;
}

/**
 * One concern's pair of sentences, keyed by the `value` the server returns for it.
 *
 * `value` is NOT derivable from the display name — "Hair Breakage" comes back as `HAIR_FALL` while
 * "Hairfall / Hair Loss" is `HAIR_LOSS`. These lists are the values from the MainConcern table, so
 * do not add near-miss spellings: an extra alias in the wrong group silently steals another
 * concern's copy.
 */
interface ConcernExamples {
  values: string[];
  texts: [string, string];
}

/** Group keys needed outside their map — the per-class fallbacks. */
const REGULAR_HAIR_CARE = 'REGULAR_HAIR_CARE';
const NO_CONCERN = 'NO_CONCERN';

function hairConcernExamples(): Record<string, ConcernExamples> {
  // Shared with "Regular Hair Care", which opens on the same Mumbai line.
  const mumbaiWeakensRoots = $localize`:@@locExMumbaiHairRoots:Mumbai’s humidity and sweat buildup can weaken hair roots`;
  // Shared with "Regular Hair Care", which closes on the same Jaipur line.
  const jaipurBreakage = $localize`:@@locExJaipurBreakage:Jaipur’s dry desert air strips moisture from strands, causing breakage`;

  return {
    // "Hairfall / Hair Loss"
    HAIR_LOSS: {
      values: ['HAIR_LOSS'],
      texts: [
        mumbaiWeakensRoots,
        $localize`:@@locExChennaiHardWaterHairfall:Chennai’s hard water strips natural oils from scalp, increasing hair fall`,
      ],
    },
    // "Dandruff", plus the "Dry Itchy Scalp" concern that carries the same description
    DANDRUFF: {
      values: ['DANDRUFF', 'DRY_ITCHY_SCALP'],
      texts: [
        $localize`:@@locExKolkataDandruffFungus:Kolkata’s humidity creates the perfect conditions for dandruff-causing fungus`,
        $localize`:@@locExDelhiWinterFlaking:Delhi’s winter dryness can trigger severe flaking and itchy scalp`,
      ],
    },
    // "Frizzy hair"
    FRIZZY_HAIR: {
      values: ['FRIZZY_HAIR'],
      texts: [
        $localize`:@@locExMumbaiFrizz:Mumbai’s moisture-heavy air is a major cause of uncontrollable frizz`,
        $localize`:@@locExHyderabadFrizz:Hyderabad’s heat and humidity make frizzy hair harder to manage`,
      ],
    },
    // "Hair Breakage" — HAIR_FALL, not HAIR_LOSS. "Brittle / Dry Hair" and "Hair Treatment Damage"
    // share its description, so they read as the same concern.
    HAIR_FALL: {
      values: ['HAIR_FALL', 'BRITTLE_DRY_HAIR', 'HAIR_TREATMENT_DAMAGE'],
      texts: [
        $localize`:@@locExAhmedabadBrittleHair:Ahmedabad’s hard water makes hair brittle and prone to breakage`,
        jaipurBreakage,
      ],
    },
    // "White / Grey Hair"
    GREY_HAIR: {
      values: ['GREY_HAIR'],
      texts: [
        $localize`:@@locExBangaloreGreying:Bangalore’s high-stress work culture is linked to premature greying`,
        $localize`:@@locExDelhiGreying:Delhi’s pollution and air toxins can accelerate greying in your 20s`,
      ],
    },
    // "Thinning Hair", plus "Loss Of Volume" (both are reducing density/thickness)
    HAIR_THINNING: {
      values: ['HAIR_THINNING', 'LOSS_OF_VOLUME'],
      texts: [
        $localize`:@@locExPuneThinning:Pune’s hard water buildup on the scalp can gradually thin your hair`,
        $localize`:@@locExChennaiThinning:Chennai’s heat and UV exposure weakens follicles, causing thinning`,
      ],
    },
    // "Regular Hair Care" — the CARE-section option, and the HAIR fallback
    [REGULAR_HAIR_CARE]: {
      values: [REGULAR_HAIR_CARE],
      texts: [mumbaiWeakensRoots, jaipurBreakage],
    },
  };
}

function faceConcernExamples(): Record<string, ConcernExamples> {
  // Both shared with "Better, Glowing Skin", which shows the same two lines.
  const kolkataDullSkin = $localize`:@@locExKolkataDullSkin:Kolkata’s humidity and pollution leave a layer of grime, making skin look dull`;
  const delhiStripsGlow = $localize`:@@locExDelhiGlow:Delhi’s smog and dust settle on your skin daily, stripping your glow`;

  return {
    // "Acne / Pimples", plus the acne-family concerns that sit under it
    ACNE_OR_PIMPLES: {
      values: ['ACNE_OR_PIMPLES', 'COMEDONES', 'BLACKHEADS', 'WHITEHEADS', 'ROSACEA'],
      texts: [
        $localize`:@@locExMumbaiAcne:Mumbai’s humidity and sweat clog pores, triggering acne`,
        $localize`:@@locExDelhiAcne:Delhi’s pollution coats your skin with toxins, making acne worse`,
      ],
    },
    // "Pigmentation", plus "Melasma" (its own description names melasma)
    PIGMENTATION: {
      values: ['PIGMENTATION', 'MELASMA'],
      texts: [
        $localize`:@@locExChennaiPigmentation:Chennai’s intense sun is a leading cause of stubborn pigmentation`,
        $localize`:@@locExKolkataPigmentation:Kolkata’s humidity traps heat in your skin, darkening pigmented patches`,
      ],
    },
    // "Dark Spots / Marks", plus "Freckles"
    DARK_SPOTS_OR_MARK: {
      values: ['DARK_SPOTS_OR_MARK', 'FRECKLES'],
      texts: [
        $localize`:@@locExBangaloreDarkSpots:Bangalore’s unpredictable sun can cause dark spots even on cloudy days`,
        $localize`:@@locExHyderabadDarkSpots:Hyderabad’s harsh summers leave dark spots that take months to fade`,
      ],
    },
    // "Acne Scars"
    ACNE_SCARS: {
      values: ['ACNE_SCARS'],
      texts: [
        $localize`:@@locExMumbaiAcneScars:Mumbai’s humid air slows healing, making acne scars harder to fade`,
        $localize`:@@locExDelhiAcneScars:Delhi’s pollution weakens your skin barrier, making scars deeper`,
      ],
    },
    // "acne mark"
    ACNE_MARKS: {
      values: ['ACNE_MARKS'],
      texts: [
        $localize`:@@locExPuneAcneMarks:Pune’s hard water irritates acne-prone skin, leaving stubborn acne marks`,
        $localize`:@@locExChennaiAcneMarks:Chennai’s heat and sweat inflame acne, worsening marks left behind`,
      ],
    },
    // "Under Eye Dark Circles", plus "Puffy Eyes"
    UNDER_EYE_DARK_CIRCLES: {
      values: ['UNDER_EYE_DARK_CIRCLES', 'PUFFY_EYES'],
      texts: [
        $localize`:@@locExBangaloreDarkCircles:Bangalore’s screen-heavy lifestyle is a major cause of dark circles`,
        $localize`:@@locExDelhiDarkCircles:Delhi’s air pollution damages under-eye skin, deepening dark circles`,
      ],
    },
    // The delivered copy covers ageing, but WRINKLES / FINE_LINES are only secondary concerns today,
    // so this group is unreachable until one of them is promoted to a primary.
    WRINKLES: {
      values: ['WRINKLES', 'FINE_LINES'],
      texts: [
        $localize`:@@locExDelhiWrinkles:Delhi’s pollution breaks down collagen, causing premature wrinkles`,
        $localize`:@@locExJaipurAgeing:Jaipur’s dry desert climate strips skin moisture, accelerating ageing`,
      ],
    },
    // "Dull Skin", whose keywords also cover dry/tanned skin
    DULL_SKIN: {
      values: ['DULL_SKIN', 'DRY_SKIN_FACE'],
      texts: [kolkataDullSkin, delhiStripsGlow],
    },
    // "Better, Glowing Skin" — the CARE-section option, and the FACE fallback
    [NO_CONCERN]: {
      values: [NO_CONCERN],
      texts: [kolkataDullSkin, delhiStripsGlow],
    },
  };
}

/** The user's primary concern, as `main-concern` knows it before it has been saved. */
export interface LocationExampleConcern {
  concernClass: string;
  /** Server concern `value` from the MainConcern table, e.g. `HAIR_LOSS`. */
  concernValue?: string;
}

/**
 * Resolves the two example cards for a concern. BODY (and any unknown class) reads as FACE, which
 * is what the concern page itself defaults to. A value we do not list falls back to the class's
 * generic option, so there is always copy.
 */
export function resolveLocationExamples(concern: LocationExampleConcern): LocationExample[] {
  const isHair = concern.concernClass === ApiClientConstant.Regimen.Class.HAIR;
  const groups = isHair ? hairConcernExamples() : faceConcernExamples();
  const concernValue = (concern.concernValue || '').toUpperCase();

  const fallbackKey = isHair ? REGULAR_HAIR_CARE : NO_CONCERN;
  const matched = Object.values(groups)
    .find((group: ConcernExamples): boolean => group.values.includes(concernValue));

  return (matched ?? groups[fallbackKey]).texts
    .map((text: string, index: number): LocationExample => ({ iconSrc: EXAMPLE_ICONS[index], text }));
}
